import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { suggestionsApi } from "./api";
import { Breadcrumbs } from "../AdminLayout";
import { formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { Inbox, Check, X as XIcon, GitMerge, Play, ExternalLink, RefreshCw } from "lucide-react";

const COLL_TO_TYPE_ROUTE = {
  articles: "articles", conditions: "conditions", faqs: "faqs", guidelines: "guidelines",
  research_papers: "research-papers", research: "research-updates",
  evidence_summaries: "evidence-summaries",
  treatments: "treatments", symptoms: "symptoms", investigations: "investigations",
  medicines: "medicines", trimesters: "trimesters",
  videos: "videos", patient_guides: "patient-guides", infographics: "infographics",
  books: "books", testimonials: "testimonials", media_appearances: "media-appearances",
};

export default function SuggestionsInbox() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, accepted: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([suggestionsApi.list({ status: tab, limit: 200 }), suggestionsApi.counts()])
      .then(([r, c]) => { setRows(r); setCounts(c); })
      .catch(e => toast.error(formatErr(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tab]);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await suggestionsApi.scan();
      toast.success(`Scan complete — ${res.new_suggestions} new suggestion${res.new_suggestions !== 1 ? "s" : ""} (scanned ${res.scanned} docs)`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setScanning(false); }
  };

  const act = async (fn, id, verb) => {
    try { await fn(id); toast.success(`Suggestion ${verb}`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="suggestions-inbox">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={["Knowledge Graph", "Suggestions Inbox"]}/>
      <div className="flex items-center justify-between mt-4 mb-6">
        <div>
          <div className="font-serif text-3xl text-navy flex items-center gap-3"><Inbox size={22}/> Suggestions Inbox</div>
          <div className="mono text-[10px] uppercase tracking-widest text-prestige mt-1">Neutral relationship candidates · Auto-suggested by content scanner</div>
        </div>
        <button data-testid="btn-run-scan" onClick={runScan} disabled={scanning}
                className="bg-navy hover:bg-navy-hover disabled:opacity-60 text-white text-sm px-4 py-2 flex items-center gap-2">
          {scanning ? <RefreshCw size={14} className="animate-spin"/> : <Play size={14}/>} {scanning ? "Scanning…" : "Run scan"}
        </button>
      </div>

      <div className="flex items-center gap-1 border-b border-line mb-6" role="tablist">
        {["pending", "accepted", "rejected", "merged"].map(t => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
                  className={`mono text-[10px] uppercase tracking-widest px-4 py-2 border-b-2 -mb-px ${tab === t ? "border-navy text-navy" : "border-transparent text-stone2 hover:text-navy"}`}>
            {t} {t === "pending" && counts.pending ? `(${counts.pending})` : ""}
            {t === "accepted" && counts.accepted ? `(${counts.accepted})` : ""}
            {t === "rejected" && counts.rejected ? `(${counts.rejected})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-xs text-stone2">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-line px-6 py-12 text-center">
          <Inbox size={22} strokeWidth={1} className="mx-auto text-stone2 mb-3"/>
          <div className="text-sm text-navy">Nothing in the {tab} queue yet.</div>
          {tab === "pending" && <div className="text-xs text-stone2 mt-1">Run a scan to generate neutral link suggestions.</div>}
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map(s => {
            const srcRoute = s.source && COLL_TO_TYPE_ROUTE[s.source_type];
            const tgtRoute = s.target && COLL_TO_TYPE_ROUTE[s.target_type];
            return (
              <li key={s.id} data-testid={`sug-${s.id}`} className="bg-white border border-line p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="mono text-[10px] uppercase tracking-widest bg-[#3F6250]/10 text-[#3F6250] px-2 py-0.5">
                        {s.relationship_type}
                      </span>
                      <span className="mono text-[10px] uppercase tracking-widest text-stone2">auto-suggested · confidence {(s.confidence * 100).toFixed(0)}%</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="mono text-[10px] uppercase tracking-widest text-stone2">{s.source_type}</span>
                      {srcRoute ? (
                        <Link to={`/admin/content/${srcRoute}/${s.source.id}`} className="text-navy hover:text-terracotta inline-flex items-center gap-1">
                          {s.source?.title || s.source?.slug} <ExternalLink size={11}/>
                        </Link>
                      ) : <span className="text-navy">{s.source?.title}</span>}
                      <span className="text-stone2">→</span>
                      <span className="mono text-[10px] uppercase tracking-widest text-stone2">{s.target_type}</span>
                      {tgtRoute ? (
                        <Link to={`/admin/content/${tgtRoute}/${s.target.id}`} className="text-navy hover:text-terracotta inline-flex items-center gap-1">
                          {s.target?.title || s.target?.slug} <ExternalLink size={11}/>
                        </Link>
                      ) : <span className="text-navy">{s.target?.title}</span>}
                    </div>
                    {s.context_snippet && (
                      <div className="mt-3 text-xs text-stone2 border-l-2 border-line pl-3 italic">
                        …{s.context_snippet}…
                      </div>
                    )}
                  </div>
                  {tab === "pending" && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid={`accept-${s.id}`} onClick={() => act(suggestionsApi.accept, s.id, "accepted")}
                              className="border border-[#3F6250] text-[#3F6250] hover:bg-[#3F6250] hover:text-white text-xs px-3 py-1.5 inline-flex items-center gap-1">
                        <Check size={12}/> Accept
                      </button>
                      <button data-testid={`merge-${s.id}`} onClick={() => act(suggestionsApi.merge, s.id, "merged")}
                              className="border border-navy text-navy hover:bg-navy hover:text-white text-xs px-3 py-1.5 inline-flex items-center gap-1">
                        <GitMerge size={12}/> Merge
                      </button>
                      <button data-testid={`reject-${s.id}`} onClick={() => act(suggestionsApi.reject, s.id, "rejected")}
                              className="border border-[#9F1239] text-[#9F1239] hover:bg-[#9F1239] hover:text-white text-xs px-3 py-1.5 inline-flex items-center gap-1">
                        <XIcon size={12}/> Reject
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
